// Bộ chọn nhánh / đơn vị của trang "Gửi thông báo". Máy tính: khung danh sách ngay trong form; điện thoại: nút mở
// Sheet có ô tìm + danh sách tick (chọn theo data-viewport — useIsMobile, không theo bề rộng). Mỗi hàng là một
// <label> cao 44px trên điện thoại: bấm cả hàng là tick.

import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { networkMatcher } from '@shoptrack/common/network-tree'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { Sheet } from '@/components/ui/sheet'
import { SkeletonRows } from '@/components/ui/skeleton'
import { LoadError } from '@/components/ui/data-notice'
import { UNIT_TYPE_LABEL, UNIT_TYPE_ORDER, UNIT_TYPE_TONE, fmtNum, type AnnounceUnitType } from './types'

export interface PickerItem {
  id: string
  name: string
  tenantType: AnnounceUnitType
  parId: string | null
  /** Dòng phụ: số đơn vị trong nhánh, hoặc cấp trên */
  sub?: string
  /** Chữ thêm cho ô tìm (tên chủ, SĐT) */
  search?: Array<string | null | undefined>
}

/** Vẽ tối đa ngần này hàng — NPP lớn có hàng nghìn Shop, gõ tìm để thu hẹp. */
const RENDER_CAP = 300

export function UnitPicker({
  title,
  items,
  selected,
  onChange,
  loading,
  error,
  onRetry,
}: {
  /** "Chọn nhánh" | "Chọn đơn vị" — nhãn nút mở trên điện thoại + tiêu đề Sheet */
  title: string
  items: PickerItem[]
  selected: string[]
  onChange: (ids: string[]) => void
  loading?: boolean
  error?: boolean
  onRetry?: () => void
}) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const sel = useMemo(() => new Set(selected), [selected])
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items])
  const names = selected.map((id) => byId.get(id)?.name).filter((n): n is string => !!n)

  const body = (
    <PickerList items={items} sel={sel} onChange={onChange} loading={loading} error={error} onRetry={onRetry} sticky={isMobile} />
  )

  if (!isMobile) {
    return (
      <div className="rounded-lg border border-rule bg-raise" role="group" aria-label={title}>
        {body}
      </div>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="motion-state flex min-h-11 w-full items-center gap-3 rounded-lg border border-rule bg-raise px-3.5 text-left text-sm hover:bg-paperSoft"
        aria-haspopup="dialog"
      >
        <span className={cn('min-w-0 flex-1 truncate', names.length ? 'text-ink' : 'text-inkSoft')}>
          {names.length ? names.slice(0, 2).join(', ') + (names.length > 2 ? ` +${fmtNum(names.length - 2)}` : '') : title}
        </span>
        {names.length > 0 && <span className="shrink-0 rounded-full bg-accent/12 px-2 py-0.5 text-xs font-medium tabular-nums text-accent">{fmtNum(names.length)}</span>}
        <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden />
      </button>
      <Sheet open={open} onOpenChange={setOpen} title={title}>
        {body}
        <div className="sticky -bottom-4 -mx-4 -mb-4 mt-2 border-t border-rule bg-raise px-4 py-3 sm:-bottom-6 sm:-mx-6 sm:-mb-6 sm:px-6">
          <Button type="button" className="w-full" onClick={() => setOpen(false)}>
            Đóng{names.length ? ` · ${fmtNum(names.length)}` : ''}
          </Button>
        </div>
      </Sheet>
    </>
  )
}

function PickerList({
  items,
  sel,
  onChange,
  loading,
  error,
  onRetry,
  sticky,
}: {
  items: PickerItem[]
  sel: Set<string>
  onChange: (ids: string[]) => void
  loading?: boolean
  error?: boolean
  onRetry?: () => void
  /** Trong Sheet: ô tìm dính đầu khi cuộn */
  sticky: boolean
}) {
  const [text, setText] = useState('')
  const [type, setType] = useState<AnnounceUnitType | 'all'>('all')
  const presentTypes = useMemo(() => UNIT_TYPE_ORDER.filter((t) => items.some((i) => i.tenantType === t)), [items])
  const typeCount = useMemo(() => {
    const c: Record<string, number> = {}
    for (const i of items) c[i.tenantType] = (c[i.tenantType] ?? 0) + 1
    return c
  }, [items])

  const visible = useMemo(() => {
    const match = networkMatcher<PickerItem>(text, (i) => i.search ?? [])
    return items.filter((i) => (type === 'all' || i.tenantType === type) && (!match || match(i)))
  }, [items, text, type])
  const shown = visible.slice(0, RENDER_CAP)

  const visibleSelected = visible.filter((i) => sel.has(i.id)).length
  const allChecked = visible.length > 0 && visibleSelected === visible.length
  const someChecked = visibleSelected > 0 && !allChecked
  const allRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = someChecked
  }, [someChecked])

  const toggle = (id: string) => {
    const next = new Set(sel)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange([...next])
  }
  const toggleAll = () => {
    const next = new Set(sel)
    if (allChecked) for (const i of visible) next.delete(i.id)
    else for (const i of visible) next.add(i.id)
    onChange([...next])
  }

  return (
    <div>
      <div className={cn('space-y-2 border-b border-rule p-2', sticky && 'sticky top-0 z-10 -mx-4 -mt-4 bg-raise px-4 pb-3 pt-4 sm:-mx-6 sm:-mt-6 sm:px-6 sm:pt-6')}>
        <SearchInput value={text} onChange={(e) => setText(e.target.value)} placeholder="Tìm tên, SĐT…" aria-label="Tìm đơn vị" />
        {presentTypes.length > 1 && (
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Loại đơn vị">
            {(['all', ...presentTypes] as const).map((t) => (
              <Button
                key={t}
                type="button"
                size="sm"
                variant={type === t ? 'default' : 'outline'}
                aria-pressed={type === t}
                onClick={() => setType(t)}
              >
                {t === 'all' ? 'Tất cả' : UNIT_TYPE_LABEL[t]}
                <span className="tabular-nums opacity-70">{fmtNum(t === 'all' ? items.length : typeCount[t])}</span>
              </Button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <SkeletonRows rows={5} className="p-3" />
      ) : error ? (
        <LoadError onRetry={onRetry} />
      ) : visible.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted">{items.length ? 'Không có đơn vị nào khớp.' : 'Chưa có đơn vị'}</p>
      ) : (
        <div className={cn(!sticky && 'max-h-80 overflow-y-auto')}>
          <label className="flex min-h-11 cursor-pointer items-center gap-3 border-b border-rule/60 px-3 py-1.5 text-sm text-inkSoft hover:bg-paperSoft desktop:min-h-9">
            <input ref={allRef} type="checkbox" className="size-4 shrink-0 cursor-pointer accent-accent" checked={allChecked} onChange={toggleAll} />
            <span className="flex-1">Chọn tất cả</span>
            <span className="tabular-nums text-muted">{fmtNum(visibleSelected)}/{fmtNum(visible.length)}</span>
          </label>
          <ul className="py-1">
            {shown.map((i) => (
              <li key={i.id}>
                <label className="flex min-h-11 cursor-pointer items-center gap-3 px-3 py-1.5 hover:bg-paperSoft desktop:min-h-10">
                  <input type="checkbox" className="size-4 shrink-0 cursor-pointer accent-accent" checked={sel.has(i.id)} onChange={() => toggle(i.id)} />
                  <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-xs bg-toneBg text-toneFg', UNIT_TYPE_TONE[i.tenantType])}>{UNIT_TYPE_LABEL[i.tenantType]}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{i.name}</span>
                    {i.sub && <span className="block truncate text-xs text-muted">{i.sub}</span>}
                  </span>
                </label>
              </li>
            ))}
            {visible.length > shown.length && (
              <li className="px-3 py-2 text-center text-xs tabular-nums text-muted">+{fmtNum(visible.length - shown.length)}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
